import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { useChallengeStorage } from './hooks/useChallengeStorage';
import { Zap, Crown, Trophy, Activity, Loader2, Check } from 'lucide-react';
import './PricingPage.css';
import TrustAndFAQ from './TrustAndFAQ';

const PLAN_CONFIGS = {
  free: {
    name: 'Free',
    price: 0,
    priceId: null,
    tagline: 'Get a feel for it',
    features: [
      { text: '30 Solo Challenges / month', included: true },
      { text: '10 Ranked Battles / month', included: true },
      { text: '2-min & 5-min formats', included: true },
      { text: 'Full score breakdown', included: true },
      { text: 'ELO rating & leaderboard', included: true },
      { text: 'Session history', included: true },
      { text: '10-min Deep Dive format', included: false },
      { text: 'Challenge a friend', included: false },
    ],
    highlight: false,
    badge: null,
    icon: null,
  },
  starter: {
    name: 'Starter',
    price: 7,
    priceId: 'price_1SGwtUSgNwWzmJhmkdqu8lQV',
    tagline: 'For people getting serious',
    features: [
      { text: '200 Solo Challenges / month', included: true },
      { text: 'Unlimited Ranked Battles', included: true },
      { text: '2-min & 5-min formats', included: true },
      { text: 'Full score breakdown', included: true },
      { text: 'ELO rating & leaderboard', included: true },
      { text: 'Session history', included: true },
      { text: '10-min Deep Dive format', included: false },
      { text: 'Challenge a friend', included: false },
    ],
    highlight: false,
    badge: null,
    icon: Zap,
  },
  pro: {
    name: 'Pro',
    price: 15,
    priceId: 'price_1SGwuhSgNwWzmJhmQrYBePbX',
    tagline: 'The full experience',
    features: [
      { text: '500 Solo Challenges / month', included: true },
      { text: 'Unlimited Ranked Battles', included: true },
      { text: 'All formats incl. 10-min Deep Dive', included: true },
      { text: 'Full score breakdown', included: true },
      { text: 'ELO rating & leaderboard', included: true },
      { text: 'Session history', included: true },
      { text: 'Challenge a friend', included: true },
      { text: 'Performance trend analytics', included: true },
    ],
    highlight: true,
    badge: 'Most Popular',
    icon: Crown,
  },
  premium: {
    name: 'Premium',
    price: 30,
    priceId: 'price_1SGwvGSgNwWzmJhmbKlJO3ST',
    tagline: 'For the top of the leaderboard',
    features: [
      { text: '1400 Solo Challenges / month', included: true },
      { text: 'Unlimited Ranked Battles', included: true },
      { text: 'All formats incl. 10-min Deep Dive', included: true },
      { text: 'Full score breakdown', included: true },
      { text: 'ELO rating & leaderboard', included: true },
      { text: 'Session history', included: true },
      { text: 'Challenge a friend', included: true },
      { text: 'Performance trend analytics', included: true },
    ],
    highlight: false,
    badge: null,
    icon: Trophy,
  },
};

const PricingPage = () => {
  const { user } = useAuth();
  const { userPlan, getUsageDisplay } = useChallengeStorage();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);

  const usageDisplay = getUsageDisplay();

  const getPlanStatus = (planKey) => {
    if (!user) return 'login';
    if ((userPlan || 'free') === planKey) return 'current';
    const order = ['free', 'starter', 'pro', 'premium'];
    const cur = order.indexOf(userPlan || 'free');
    const tgt = order.indexOf(planKey);
    if (tgt > cur) return 'upgrade';
    if (tgt < cur) return 'downgrade';
    return 'available';
  };

  const handleUpgrade = async (planKey) => {
    if (!user) return;
    const plan = PLAN_CONFIGS[planKey];
    if (!plan.priceId) return;

    setIsLoading(true);
    setLoadingPlan(planKey);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: plan.priceId,
          userId: user.uid,
          userEmail: user.email,
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.url) window.location.href = data.url;
      else throw new Error('No checkout URL received');
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start checkout. Please try again or contact support.');
    } finally {
      setIsLoading(false);
      setLoadingPlan(null);
    }
  };

  const handleCTAClick = (planKey) => {
    const status = getPlanStatus(planKey);
    if (planKey === 'free') return;
    if (status === 'upgrade') handleUpgrade(planKey);
    if (status === 'downgrade') alert('Contact support@promptiya.com to downgrade.');
  };

  const getButtonText = (planKey) => {
    if (loadingPlan === planKey) return 'Processing...';
    const status = getPlanStatus(planKey);
    if (planKey === 'free') return user ? 'Your current plan' : 'Start free';
    switch (status) {
      case 'current':   return 'Current plan';
      case 'login':     return `Get ${PLAN_CONFIGS[planKey].name}`;
      case 'upgrade':   return `Upgrade to ${PLAN_CONFIGS[planKey].name}`;
      case 'downgrade': return 'Contact support';
      default:          return `Get ${PLAN_CONFIGS[planKey].name}`;
    }
  };

  const isDisabled = (planKey) => {
    const status = getPlanStatus(planKey);
    return (
      status === 'current' ||
      (planKey === 'free' && !!user) ||
      isLoading
    );
  };

  return (
    <div className="prc-root">

      {/* ── Header ── */}
      <div className="prc-header">
        <p className="prc-eyebrow">Pricing</p>
        <h1 className="prc-title">Simple Pricing.</h1>
        <p className="prc-subtitle">
          Everything core is free. Pay when you want more volume or the full format set.
        </p>

        {user && usageDisplay && (
          <div className="prc-usage-strip">
            <Activity size={15} />
            <span>
              {usageDisplay.monthlyRemaining} of {usageDisplay.monthlyLimit} challenges left this month
              &nbsp;·&nbsp; {usageDisplay.totalCompleted} completed total
            </span>
          </div>
        )}
      </div>

      {/* ── Cards ── */}
      <div className="prc-grid">
        {Object.entries(PLAN_CONFIGS).map(([planKey, plan]) => {
          const status = getPlanStatus(planKey);
          const Icon = plan.icon;

          return (
            <div
              key={planKey}
              className={[
                'prc-card',
                plan.highlight ? 'prc-card--featured' : '',
                status === 'current' ? 'prc-card--current' : '',
              ].join(' ')}
            >
              {plan.badge && <div className="prc-badge">{plan.badge}</div>}

              <div className="prc-card-top">
                <div className="prc-plan-name-row">
                  {Icon && <Icon size={16} className="prc-plan-icon" />}
                  <span className="prc-plan-name">{plan.name}</span>
                </div>
                <p className="prc-plan-tagline">{plan.tagline}</p>
                <div className="prc-price-row">
                  {plan.price === 0 ? (
                    <span className="prc-price">Free</span>
                  ) : (
                    <>
                      <span className="prc-price-currency">$</span>
                      <span className="prc-price">{plan.price}</span>
                      <span className="prc-price-period">/mo</span>
                    </>
                  )}
                </div>
              </div>

              <ul className="prc-features">
                {plan.features.map((f, i) => (
                  <li key={i} className={`prc-feature ${!f.included ? 'prc-feature--off' : ''}`}>
                    <span className="prc-feature-dot">
                      {f.included ? <Check size={13} /> : '—'}
                    </span>
                    {f.text}
                  </li>
                ))}
              </ul>

              <button
                className={[
                  'prc-cta',
                  plan.highlight ? 'prc-cta--featured' : '',
                  status === 'current' || (planKey === 'free' && !!user) ? 'prc-cta--current' : '',
                ].join(' ')}
                disabled={isDisabled(planKey)}
                onClick={() => handleCTAClick(planKey)}
              >
                {loadingPlan === planKey && <Loader2 size={14} className="prc-spinner" />}
                {getButtonText(planKey)}
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Feature comparison table ── */}
      <div className="prc-table-wrap">
        <h2 className="prc-table-title">What's actually included</h2>
        <table className="prc-table">
          <thead>
            <tr>
              <th></th>
              <th>Free</th>
              <th>Starter</th>
              <th>Pro</th>
              <th>Premium</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Solo Challenges / month', '30', '200', '500', '1,400'],
              ['Ranked Battles / month', '10', 'Unlimited', 'Unlimited', 'Unlimited'],
              ['2-min & 5-min formats', '✓', '✓', '✓', '✓'],
              ['10-min Deep Dive', '—', '—', '✓', '✓'],
              ['Full score breakdown', '✓', '✓', '✓', '✓'],
              ['ELO & leaderboard', '✓', '✓', '✓', '✓'],
              ['Session history', '✓', '✓', '✓', '✓'],
              ['Challenge a friend', '—', '—', '✓', '✓'],
              ['Performance analytics', '—', '—', '✓', '✓'],
            ].map(([label, ...vals]) => (
              <tr key={label}>
                <td className="prc-table-label">{label}</td>
                {vals.map((v, i) => (
                  <td key={i} className={v === '—' ? 'prc-table-no' : 'prc-table-yes'}>{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TrustAndFAQ />
    </div>
  );
};

export default PricingPage;